import { ImportMode } from './GuidelineHubInformationDto';
import { OrganisationDto } from './OrganisationDto';
import { User } from './user.model';
import { UserDto } from './UserDto';

/**
 * Information DTO about the original guideline
 * @property {string} uniqueId - The unique identifier of the original guideline
 * @property {UserDto[]} authors - The authors of the original guideline
 * @property {UserDto} owner - The owner of the original guideline
 * @property {string} version - The version of the original guideline
 * @property {Date} publishedAt - The date when the original guideline was published
 */
export interface OriginalGuidelineInformationDto {
  uniqueId: string;
  authors: UserDto[];
  owner: UserDto;
  publisher: UserDto;
  version: string;
  publishedAt: Date;
}

/**
 * Information DTO about connected guideline settings
 * @property {OriginalGuidelineInformationDto} originalGuidelineInformation - The original guideline information
 * @property {boolean} automaticUpdates - Indicates whether updates on the original guideline are automatically propagated
 * @property {boolean} forwardEmailNotifications - Indicates whether update notifications are forwarded to current organisation's users
 */
export interface ConnectedGuidelineInformationDto {
  originalGuidelineInformation: OriginalGuidelineInformationDto;
  automaticUpdates: boolean;
  forwardEmailNotifications: boolean;
  allowLocalChanges: boolean;
  allowDetachGuideline: boolean;
  importDate: Date;
  importer: User;
}

/**
 * Indicates whether the Guideline was imported from a different organization
 * or was created within the user's organization.
 */
export enum GuidelineOrigin {
  DEFAULT = 1,
  IMPORTED = 2,
  SHARED = 3,
}

export enum UpdateStatus {
  DEFAULT = 0,
  ACCEPTED = 1,
  UPDATED = 2,
  UPDATED_WITH_CONFLICTS = 3,
}

export type GuidelineImport = {
  id?: string;
  originalGuidelineUniqueId?: string;
  targetOrganisationId?: string;
  providerOrganisation?: OrganisationDto;
  automaticUpdates?: boolean;
  forwardEmailNotifications?: boolean;
  isDetached?: boolean;
  importMode?: ImportMode;
};

export type GuidelineSourceSettings = {
  automaticUpdates?: boolean;
  forwardEmailNotifications?: boolean;
};

/**
 * Information about the original guideline
 * @property {string} uniqueId - The unique identifier of the original guideline
 * @property {User[]} authors - The authors of the original guideline
 * @property {User} owner - The owner of the original guideline
 * @property {string} version - The version of the original guideline
 * @property {Date} publishedAt - The date when the original guideline was published
 */
export interface OriginalGuidelineInformation {
  uniqueId: string;
  authors: User[];
  owner: User;
  publisher: User;
  version: string;
  publishedAt: Date;
}

/**
 * Information about connected guideline settings
 * @property {OriginalGuidelineInformation} originalGuidelineInformation - The original guideline information
 * @property {boolean} automaticUpdates - Indicates whether updates on the original guideline are automatically propagated
 * @property {boolean} forwardEmailNotifications - Indicates whether update notifications are forwarded to current organisation's users
 */
export interface ConnectedGuidelineInformation {
  originalGuidelineInformation: OriginalGuidelineInformation;
  automaticUpdates: boolean;
  forwardEmailNotifications: boolean;
  allowLocalChanges: boolean;
  allowDetachGuideline: boolean;
  importDate: Date;
  importer: User;
}
